'use strict';

import React, { useState, useEffect, useRef } from 'react';
import { Route, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import cn from 'classnames/bind';

		//const colors = [ 'white', 'red', 'orange', 'yellow', 'blue', 'purple', 'black' ];

import { appSelectors, appOperations } from '@/state/domains/app';
import { ordersSelectors, ordersOperations } from '@/state/domains/orders';
import { assignmentsSelectors, assignmentsOperations } from '@/state/domains/assignments';
import { attemptsOperations, attemptsSelectors } from '@/state/domains/attempts';
import { locationListSelectors, locationListOperations } from '@/state/domains/locationList';
import { errorsSelectors, errorsOperations } from '@/state/domains/errors';

import {
	waypointsFromAssigned,
	geosFromAssigned,
	partitionWaypoints,
	getPartitions,
	flagColors,
} from '@/state/utilities';

import p from './styles.scss';

const px = cn.bind(p);

class GoogleMaps {

	static mapProps = {
		mapTypeControl: false,
		streetViewControl: true,
	};

	static rendererProps = {
		draggable: false,
		suppressMarkers: true,
		preserveViewport: true,
	};

	constructor(google, ctx) {

		this.google = google;
		this.googleMaps = this.google.maps;
		this.directionsService = new this.googleMaps.DirectionsService();
		this.renderers = [];
		this.ctx = ctx;
	}

	setMap(el) {

		this.markers = [];
		this.toCache = [];
		this.map = new this.googleMaps.Map(
			el,
			GoogleMaps.mapProps,
		);
	}

	newRenderer() {

		const renderer = new this.googleMaps.DirectionsRenderer({
			...GoogleMaps.rendererProps,
			map: this.map,
		});

		this.renderers.push(renderer);

		return renderer;
	}

	clearMap() {
		this.map = null;
		this.renderers = [];
	}

	markerIconProps(ctx, color) {

		const { someAttempted, allAttempted, served, badAddress } = ctx;

		let strokeWeight = 1;
		let fillColor = color;
		let scale = .45;

		if (someAttempted) {
			fillColor = '#bbb';
		}

		if (someAttempted && allAttempted) {
			strokeWeight = 0;
			scale = .35;

			if (served) {
				fillColor = '#2686d8';

			} else if (badAddress) {
				fillColor = '#8654c7';

			} else {
				fillColor = '#666';
			}
		}

		return {
			path: 'M22-48h-44v43h16l6 5 6-5h16z',
			//path: 'M22 1h-40v40h15.093l5.439 8.05 5.44-8.05h14.028z',
			fillColor,
			fillOpacity: 1,
			//strokeColor: 'black',
			//strokeWeight,
			labelOrigin: new this.googleMaps.Point(1.25, -25),
			scale,
		};
	}

	markerLabelProps(ctx, _color, index) {

		const { allAttempted, someAttempted, served, badAddress } = ctx;
		const text = someAttempted ? '✓' : `${ index + 1 }`;

		let color = _color === '#161616' ? '#fff' : '#444';

		if (someAttempted && allAttempted) {
			color = '#fff';

			if (served) {
			} else if (badAddress) {
			}
		}

		return { 
			text,
			fontSize: '1em',
			fontWeight: 'bold',
			color,
			zIndex: index,
		};
	}

	createMarker(ctx, locationItem, index) {

		const title = addressLine1(locationItem.location);
		const color = locationItem.flag ? flagColors[ locationItem.flag ] : '#fff';

		return new this.googleMaps.Marker({
			map: this.map,
			position: locationItem.geo,
			//icon: `https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_${ color }${ i + 1 }.png`,
			icon: this.markerIconProps(ctx, color),
			label: this.markerLabelProps(ctx, color, index),
			title,
		});
	}

	makeMarker(ctx, index) {

		const { locationItem, attempts } = ctx;
		const { location } = locationItem;
		const { jobs } = locationItem;

		const items = jobs.reduce(
			(items, job) => {

				const loc = job.locations.find(l => l.locationId === location._id);

				const isAttempted = attempts.find(a => (
					a.jobId === job._id &&
					a.locationId === location._id
				));

				if (loc) {

					const recipient = loc.recipient || '"Jane/John Doe"';

					return [
						...items,
						{
							recipient,
							task: loc.task,
							isAttempted,
							completed: loc.completed,
							invalid: loc.invalid,
							job,
						},
					];
				}

				return items;
			},
			[]
		);

		const someAttempted = items.find(i => i.isAttempted);
		const allAttempted = items.reduce(
			(isAttempted, item) => {
				if (isAttempted) {
					return item.isAttempted;
				}
				return isAttempted;
			},
			true
		);

		const served = (
			items.length === 1 &&
			items[ 0 ].completed &&
			!items[ 0 ].invalid
		);

		const badAddress = (
			items.length === 1 &&
			items[ 0 ].completed &&
			items[ 0 ].invalid
		);

		const _ctx = { someAttempted, allAttempted, served, badAddress };

		const marker = this.createMarker(_ctx, locationItem, index);
		const address1 = addressLine1(locationItem.location);

		const infowindow = new this.googleMaps.InfoWindow({
			content:
				'<div>' +
					locationItem.jobs.map(job => {
						const locObj = job.locations.find(
							l => l.placeId === location.placeId
						);
						if (locObj) {
							return `<div>${ locObj.recipient }</div>`;
						}
					}).filter(r => r) +
					(address1 ? `<div>${ address1 }</div>` : '') +
					`<div>${ location.city }, ${ location.state } ${ location.zip }</div>` +
				'</div>',
		});

		marker.addListener('click', () => {
			infowindow.open({
				anchor: marker,
				map: this.map,
				shouldFocus: false,
			});
		});

		return marker;
	}

	makeMarkers(locationItems, attempts) {
		const ctxs = locationItems.map(locationItem => ({ locationItem, attempts }));
		this.markers = ctxs.map(this.makeMarker.bind(this));
	}

	swapMarker(locationItem, index, attempts) {
		this.markers[ index ].setMap(null);
		this.markers[ index ] = this.makeMarker({ locationItem, attempts }, index);
	}

	clearMarkers() {
		this.markers.forEach(m => {
			m.setMap(null);
		});
		this.markers = [];
		this.markers.length = 0;
	}

	
	decorateLocationItems(listItems) {
		return listItems.map(this.decorateLocationItem.bind(this));
	}

	decorateLocationItem(locationItem) {

		const { location } = locationItem;
		const { placeId, geo } = location;
		const [ lat, lng ] = geo;

		return {
			...locationItem,
			geo: new this.googleMaps.LatLng(lat, lng),
			waypoint: {
				location: { placeId },
				stopover: false,
			},
		};
	}

	fitBounds(locationItems) {

		const lngs = locationItems.map(({ geo }) => geo.lng());
		const lats = locationItems.map(({ geo }) => geo.lat());

		this.map.fitBounds({
			west: Math.min.apply(null, lngs),
			east: Math.max.apply(null, lngs),
			north: Math.min.apply(null, lats),
			south: Math.max.apply(null, lats)
		});
	};

	watchPosition() {

		const currentPosOuter = new this.googleMaps.Marker({
			clickable: false,
			/*icon: new google.maps.MarkerImage(
				'//maps.gstatic.com/mapfiles/mobile/mobileimgs2.png',
				new google.maps.Size(22,22),
				new google.maps.Point(0,18),
				new google.maps.Point(11,11)
			),*/
			icon: {
				path: this.googleMaps.SymbolPath.CIRCLE,
				fillColor: '#C8D6EC',
				fillOpacity: 0.7,
				scale: 24,
				strokeWeight: 0
			},
			cursor: 'pointer',
			draggable: false,
			flat: true,
			shadow: null,
			zIndex: 20,
			map: this.map,
		});

		const currentPosInner = new this.googleMaps.Marker({
			clickable: false,
			icon: {
				path: this.googleMaps.SymbolPath.CIRCLE,
				fillColor: '#4285F4',
				fillOpacity: 1,
				scale: 6,
				strokeColor: 'white',
				strokeWeight: 1
			},
			optimized: false,
			cursor: 'pointer',
			draggable: false,
			flat: true,
			shadow: null,
			zIndex: 21,
			map: this.map,
		});

		const currentPosCirc = new this.googleMaps.Circle({
			clickable: false,
			radius: 0,
			strokeColor: '#1bb6ff',
			strokeOpacity: 0.4,
			fillColor: '#61a0bf',
			fillOpacity: 0.4,
			strokeWeight: 1,
			zIndex: 1,
			map: this.map,
		});

		window.wpid = navigator.geolocation.watchPosition(
			pos => {
				const { coords } = pos;
				const { latitude, longitude } = coords;
				const geo = [ latitude, longitude ];

				currentPosOuter.setPosition(
					new this.googleMaps.LatLng(
						latitude,
						longitude
					)
				);
				currentPosInner.setPosition(
					new this.googleMaps.LatLng(
						latitude,
						longitude
					)
				);
				currentPosCirc.setCenter(
					new this.googleMaps.LatLng(
						latitude,
						longitude
					)
				);

			},
			err => {
			},
			{
				timeout: 5000,
				maximumAge: 0,
				enableHighAccuracy: true
			}
		);
	}

	routeCached(partitions) {

		for (const part of partitions) {
			//const renderer = new google.maps.DirectionsRenderer(rendererProps(_map));
			this.directionsRenderer.setDirections(part);
		}
	}

	routePartitions(partitions) {

		this.routeCalls = partitions.map(p => ({
			origin:			{ placeId: p[ 0 ].location.placeId },
			destination:	{ placeId: p[ p.length - 1 ].location.placeId },
			waypoints:		p.slice(1, p.length - 1),
			travelMode:		'DRIVING',
		}));

		const req = this.routeCalls.shift();

		this.directionsService.route(req, serviceCallback(this));
	}

	routeDirections(locationPartitions) {

		this.renderers.forEach(r => r.setMap(null));

		if (!locationPartitions.length) {
			return;
		}

		/*const cachedPartitions = getPartitionsFromCache(makeCacheKey(this.ctx));

		if (cachedPartitions && cachedPartitions.length) {
			this.routeCached(cachedPartitions);

		} else {*/
			const partitions = getPartitions(locationPartitions);
			this.routePartitions(partitions);
		//}
	}

	setCenter(geo) {
		this.map.setCenter(geo);
	}

	setZoom(num) {
		this.map.setZoom(num);
	}
}

const serviceCallback = googleMaps => (response, status) => {

	if (status !== 'OK') {
		console.log('Google Maps API route error: ' + status);
	}

	googleMaps.toCache.push(response);

	const renderer = googleMaps.newRenderer();

	renderer.setDirections(response);

	if (googleMaps.routeCalls.length) {
		//const directionsService = getDirectionsService();
		googleMaps.directionsService.route(
			googleMaps.routeCalls.shift(),
			serviceCallback(googleMaps)
		);

	} else {
		cachePartitions(makeCacheKey(googleMaps.ctx), googleMaps.toCache);
	}
}

//const home = new google.maps.LatLng(47.466488, -122.269768);

const addressLine1 = location => {

	const streetNumber = location.streetNumber || '';
	const streetName = location.streetName || '';

	return (`${ streetNumber } ${ streetName }`).trim();
};

const makeCacheKey = ({ username, date }) => `${ username }.${ date }.routes`;
const getPartitionsFromCache = cacheKey => window[ cacheKey ];
const cachePartitions = (cacheKey, partitions) => { window[ cacheKey ] = partitions };

const getGeoLocationByJobId = (listItems, id) => listItems.reduce(
	(found, listItem) => {
		const job = listItem.jobs.find(job => job.id === id);

		if (job) {
			return listItem.geo;
		}

		return found;
	},
	null
);

/*
	componentWillUnmount() {
		_map = null;
		this.service = null;
		navigator.geolocation.clearWatch(window.wpid);
	}

	shouldComponentUpdate(nextProps) {
		return !nextProps.isDragging;
	}

	if (date != prevProps.date) {
		fitBounds(listItems)
	}
*/

class MapCont extends React.PureComponent {

	componentDidMount() {

		const {
			google,
			username,
			date,
			listItems: _listItems,
			assignments,
			attempts,
			partitionedJobIds,
			locationPartitions,
			pinpoint,
		} = this.props;


		this.googleMap = new GoogleMaps(google, { username, date });
		
		const locationItems = this.googleMap.decorateLocationItems(assignments.locations);
		const el = document.getElementById('map-cont');

		this.googleMap.setMap(el);

		this.googleMap.makeMarkers(locationItems, attempts);
		this.googleMap.routeDirections(assignments.partitions);

		if (pinpoint) {

			const loc = locationItems.find(i => i.locationId === pinpoint);

			if (loc) {
				this.googleMap.setCenter(loc.geo);
				this.googleMap.setZoom(15);
			}
		} else {
			if (locationItems.length) {
				setTimeout(() => this.googleMap.fitBounds(locationItems), 250);
			}
		}

		//first only?
		this.googleMap.watchPosition();
	}

	componentDidUpdate(prevProps) {

		const {
			date,
			listItems: _listItems,
			assignments,
			attempts,
			partitionedJobIds,
			locationPartitions,
			pinpoint,
			lastUpdate,
		} = this.props;

		
		//const el = document.getElementById('map-cont');
		
		const locationItems = this.googleMap.decorateLocationItems(assignments.locations);

		if (date != prevProps.date) {

			this.googleMap.clearMarkers();
			this.googleMap.makeMarkers(locationItems, attempts);
			this.googleMap.routeDirections(assignments.partitions);
			this.googleMap.fitBounds(locationItems);
		}

		if (lastUpdate) {

			switch (lastUpdate.op) {
				case 'FLAG_INCREMENT':

					this.googleMap.swapMarker(
						locationItems[ lastUpdate.value.index ],
						lastUpdate.value.index,
						attempts
					);
					break;

				case 'REORDERED_LOCATIONS':
				case 'REVERSE_SELECTION':
				case 'OPTIMIZE_ROUTE':
				case 'DELETE_ITEMS':
				case 'ADD_TO_ROUTE':

					this.googleMap.clearMarkers();
					this.googleMap.makeMarkers(locationItems, attempts);
					this.googleMap.routeDirections(assignments.partitions);
					break;
			}
		}

		if (pinpoint) {

			const loc = locationItems.find(i => i.locationId === pinpoint);

			if (loc) {
				this.googleMap.setCenter(loc.geo);
				this.googleMap.setZoom(15);
			}
		} else {
			/*if (locationItems.length) {
				setTimeout(() => this.googleMap.fitBounds(locationItems), 250);
			}*/
		}
	}

	componentWillUnmount() {

		this.googleMap.clearMap();
	}

	render() {
		const {
			google,
			isDragging,
			username,
			date,
			listItems: _listItems,
			classNames,
			pinpoint,
			headerWidth,
			mobile,
		} = this.props;

		//icon: {
			//url: 'http://maps.google.com/mapfiles/ms/micons/red.png',
			//scaledSize: new google.maps.Size(20, 20),
			//origin: new google.maps.Point(0, 0),
			//anchor: new google.maps.Point(28, 28),
			//labelOrigin: new google.maps.Point(10, 7)
		//}

		const headerHeight = 54;

		const styles = {
			height: window.innerHeight - headerHeight - 5 + 'px'
		};

		if (!mobile) {
			styles.width = headerWidth - 350 + 'px'
		}

		return (
			<div id='map-cont' className={ px(classNames) } style={ styles } />
		);
	}
};

export default connect(
	(state, props) => ({
		headerWidth: appSelectors.getClientWidth(state, props),
		assignments: assignmentsSelectors.fulfillAssignments(state, props),
		attempts: attemptsSelectors.getDailyAttempts(state, props),
		listItems: locationListSelectors.getList(state, props),
		lastUpdate: locationListSelectors.getLastUpdate(state, props),
		locationPartitions: assignmentsSelectors.getLocationPartitions(state, props),
	}),
	{
		addError: errorsOperations.addError,
	}
)(MapCont);
